import React from 'react'
import { useTranslation } from 'react-i18next'
import HistoryGraph from './dashboard/HistoryGraph'

const HistoryGraphModal = ({
  isOpen,
  onClose,
  deviceIP,
  deviceName,
  logsContent,
  timeRange,
  onTimeRangeChange,
  chartData,
  isLoading,
}) => {
  const { t } = useTranslation()
  
  const getTimeRanges = () => [
    { value: '30m', label: t('modals.historyGraph.timeRanges.30m') },
    { value: '6h', label: t('modals.historyGraph.timeRanges.6h') },
    { value: '24h', label: t('modals.historyGraph.timeRanges.24h') },
    { value: '7d', label: t('modals.historyGraph.timeRanges.7d') },
    { value: '30d', label: t('modals.historyGraph.timeRanges.30d') },
  ]

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-950/70 px-4 py-10 backdrop-blur-2xl"
      role="dialog"
      aria-modal="true"
      aria-label={t('modals.historyGraph.ariaLabel', { deviceIP })}
    >
      <div className="pointer-events-none absolute inset-0 bg-brand-radial opacity-50" />

      <div className="relative z-10 w-full max-w-5xl overflow-hidden rounded-3xl border border-surface-border/40 bg-surface-raised/90 shadow-elevated-lg">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-5 top-5 inline-flex h-10 w-10 items-center justify-center rounded-full bg-surface-base/80 text-muted-200 transition hover:bg-brand-400/20 hover:text-white"
          aria-label={t('modals.historyGraph.closeAriaLabel')}
        >
          <span className="material-icons text-lg">close</span>
        </button>

        <div className="max-h-[85vh] overflow-y-auto">
          <div className="flex flex-col gap-6 p-6 md:p-8">
            <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-brand-400/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-100">
                  {t('modals.historyGraph.badge')}
                </span>
                <h3 className="mt-4 font-display text-2xl text-white md:text-3xl">
                  {t('modals.historyGraph.title', { deviceName })}
                </h3>
                <p className="mt-2 text-sm text-muted-400">
                  {t('modals.historyGraph.subtitle')}
                </p>
                <p className="mt-2 text-sm text-muted-400">
                  {t('modals.historyGraph.deviceIp')} {deviceIP}
                </p>
              </div>
            </header>

            <div className="flex flex-wrap gap-2 rounded-2xl bg-сurface-base/70 p-4 md:items-center md:justify-between">
              <div className="text-xs font-semibold uppercase tracking-[0.25em] text-muted-500">
                {t('modals.historyGraph.period')}
              </div>
              <div className="flex flex-wrap gap-2">
                {getTimeRanges().map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => onTimeRangeChange(value)}
                    className={`inline-flex items-center rounded-2xl px-4 py-2 text-xs font-semibold transition ${
                      timeRange === value
                        ? 'bg-brand-400/30 text-white shadow-brand'
                        : 'bg-surface-base/60 text-muted-200 hover:bg-brand-400/15 hover:text-white'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-3xl bg-сurface-base/60 p-5 shadow-soft">
              <HistoryGraph chartData={chartData} isLoading={isLoading} />
            </div>

            {logsContent && (
              <section className="rounded-3xl bg-сurface-base/60 p-5 shadow-soft">
                <header className="mb-3 flex items-center justify-between">
                  <p className="text-sm font-semibold text-muted-100">{t('modals.historyGraph.eventLog')}</p>
                  <span className="text-xs uppercase tracking-[0.25em] text-muted-500">{t('modals.historyGraph.live')}</span>
                </header>
                <div className="max-h-48 overflow-y-auto rounded-2xl bg-сurface-raised/70 p-4 text-xs font-mono text-muted-200">
                  <pre className="whitespace-pre-wrap break-words">{logsContent}</pre>
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default HistoryGraphModal
